import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, MoreHorizontal, BookOpen } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { INSTITUTE } from "@/lib/content";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/courses", label: "Courses" },
  { to: "/admissions", label: "Admissions" },
  { to: "/blog", label: "Blog" },
  { to: "/testimonials", label: "Testimonials" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`sticky top-0 z-50 w-full transition-all ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-black/10" : "bg-white border-b border-black/5"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-10 xl:px-14">
        <div className="flex items-center justify-between h-20 lg:h-24 gap-6">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="navbar-logo-link">
            <img src={INSTITUTE.logo_symbol} alt="Kothari Classes" className="h-10 lg:h-12 w-auto" />
            <div className="flex flex-col leading-none">
              <span className="font-brand font-bold text-lg sm:text-xl lg:text-2xl tracking-tight text-gray-950 whitespace-nowrap">
                Kothari Classes
              </span>
              <span className="hidden sm:block mt-1.5 font-brand text-[11px] lg:text-[12px] font-semibold tracking-[0.16em] text-gray-500 uppercase whitespace-nowrap">
                IIT-JEE | NEET | CET | Foundation
              </span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8 ml-auto">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`navbar-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `nav-link font-brand text-[15px] font-medium text-gray-800 hover:text-gray-950 whitespace-nowrap ${isActive ? "active text-gray-950" : ""}`
                }
              >
                {l.label}
              </NavLink>
            ))}

            {/* More menu (3-dot) */}
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="navbar-more-trigger"
                aria-label="More menu"
                className="w-9 h-9 flex items-center justify-center border border-gray-300 hover:border-red-600 hover:text-red-600 transition-colors focus:outline-none focus:ring-2 focus:ring-red-600/30"
              >
                <MoreHorizontal className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none border-gray-200 min-w-[200px] p-1">
                <DropdownMenuItem asChild>
                  <Link
                    to="/resources"
                    data-testid="navbar-more-resources"
                    className="font-brand text-sm font-medium text-gray-800 hover:text-red-600 cursor-pointer flex items-center gap-2 px-3 py-2.5 rounded-none"
                  >
                    <BookOpen className="w-4 h-4 text-red-600" />
                    Resource Center
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* CTAs */}
          <div className="hidden lg:flex items-center gap-4 xl:gap-5 shrink-0 pl-6 xl:pl-8 ml-2 border-l border-gray-200">
            <a
              href={`tel:${INSTITUTE.phone_raw}`}
              data-testid="navbar-call-link"
              className="hidden xl:flex items-center gap-2 text-sm font-medium text-gray-900 hover:text-red-600 transition-colors whitespace-nowrap"
            >
              <Phone className="w-4 h-4 text-red-600" />
              {INSTITUTE.phone_short}
            </a>
            <a
              href={`tel:${INSTITUTE.phone_raw}`}
              aria-label="Call us"
              data-testid="navbar-call-icon"
              className="xl:hidden w-10 h-10 border border-gray-300 hover:border-red-600 hover:text-red-600 flex items-center justify-center transition-colors"
            >
              <Phone className="w-4 h-4" />
            </a>
            <Link to="/admissions" data-testid="navbar-demo-button" className="btn-primary text-sm rounded-none whitespace-nowrap">
              Book Free Demo
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-gray-900"
            data-testid="navbar-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile drawer */}
        {open && (
          <div className="lg:hidden border-t border-black/10 py-4 space-y-1" data-testid="navbar-mobile-drawer">
            {[...links, { to: "/resources", label: "Resources" }].map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`navbar-mobile-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `block px-2 py-3 text-base font-medium border-l-2 transition-colors ${
                    isActive ? "text-red-600 border-red-600 bg-red-50/60" : "text-gray-900 border-transparent hover:border-gray-300"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="pt-3 flex flex-col gap-2">
              <a href={`tel:${INSTITUTE.phone_raw}`} className="btn-secondary justify-center rounded-none" data-testid="navbar-mobile-call">
                <Phone className="w-4 h-4" /> {INSTITUTE.phone}
              </a>
              <Link to="/admissions" className="btn-primary justify-center rounded-none" data-testid="navbar-mobile-demo">
                Book Free Demo
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
